import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

type YouTubePlayerInstance = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  setVolume: (volume: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  destroy: () => void;
};

type YouTubeNamespace = {
  Player: new (element: HTMLElement, options: Record<string, unknown>) => YouTubePlayerInstance;
  PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
};

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let loader: Promise<YouTubeNamespace> | null = null;

function loadYouTube() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (loader) return loader;
  loader = new Promise<YouTubeNamespace>((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
      else reject(new Error('YouTube API unavailable'));
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => reject(new Error('YouTube API failed to load'));
    document.head.append(script);
  });
  return loader;
}

export type YouTubePlayerHandle = {
  play: () => void;
  pause: () => void;
  seek: (seconds: number) => void;
  setVolume: (volume: number) => void;
};

export const YouTubePlayer = forwardRef<YouTubePlayerHandle, {
  videoId: string;
  onTime: (timeMs: number, durationMs: number) => void;
  onPlaying: (playing: boolean) => void;
  onEnded: () => void;
  onError: (message: string) => void;
}>(({ videoId, onTime, onPlaying, onEnded, onError }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YouTubePlayerInstance | null>(null);
  const intervalRef = useRef<number | null>(null);

  useImperativeHandle(ref, () => ({
    play: () => playerRef.current?.playVideo(),
    pause: () => playerRef.current?.pauseVideo(),
    seek: (seconds) => playerRef.current?.seekTo(seconds, true),
    setVolume: (volume) => playerRef.current?.setVolume(Math.round(volume * 100)),
  }), []);

  useEffect(() => {
    let cancelled = false;
    void loadYouTube()
      .then((YT) => {
        if (cancelled || !containerRef.current) return;
        playerRef.current = new YT.Player(containerRef.current, {
          videoId,
          width: '100%',
          height: '100%',
          playerVars: { controls: 1, rel: 0, modestbranding: 0, playsinline: 1, fs: 1 },
          events: {
            onReady: () => {
              intervalRef.current = window.setInterval(() => {
                const player = playerRef.current;
                if (player) onTime(player.getCurrentTime() * 1000, player.getDuration() * 1000);
              }, 120);
            },
            onStateChange: (event: { data: number }) => {
              onPlaying(event.data === YT.PlayerState.PLAYING);
              if (event.data === YT.PlayerState.ENDED) onEnded();
            },
            onError: () => onError('这支 YouTube 视频当前不允许嵌入播放'),
          },
        });
      })
      .catch((error) => onError(error instanceof Error ? error.message : 'YouTube 播放器加载失败'));
    return () => {
      cancelled = true;
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [videoId, onEnded, onError, onPlaying, onTime]);

  return <div className="youtube-player" ref={containerRef} />;
});

YouTubePlayer.displayName = 'YouTubePlayer';
